import type { CodiceContatore } from '@praticheenergia/domain'
import type { ConsumoMensile } from './consumi'
import { aggiungiRiga, ambitoDi, type QuadriInCostruzione } from './costruzione'
import { dec } from './dec'
import { ripartisciScaglioni } from './scaglioni'
import type { RigaMutabile } from './modello'
import { bloccante, type Diagnostica } from './diagnostica'
import type { DatiPeriodo } from './ingresso'
import type { ModelloDichiarazione, QuadroModello } from './modello'
import { righeDaFormule } from './formule-mensili'
import { applicaOverride } from './override'
import type { RegolePeriodo } from './regole-periodo'
import { applicaAccisa, applicaScaglioni } from './accisa'

export interface EsitoQuadri {
  readonly modello: ModelloDichiarazione
  readonly diagnostiche: readonly Diagnostica[]
}

/** Struttura del tracciato, non una regola dell'anno: i contatori X non hanno un quadro proprio. */
const QUADRO_DEL_CONTATORE: Readonly<Record<CodiceContatore, QuadroModello | undefined>> = {
  produzione: 'A',
  uso_promiscuo: 'B',
  consumi_esenti: 'C',
  consumi_assoggettati: 'E',
  ceduta: 'G',
  ricevuta: 'H',
  non_sottoposti: undefined,
  altri: undefined,
}

/** Ogni riga porta tutti i dati del contatore: e' il template a decidere quali scrivere. */
function righeDaContatori(
  q: QuadriInCostruzione,
  esclusi: ReadonlySet<QuadroModello>,
  consumi: readonly ConsumoMensile[],
  dati: DatiPeriodo,
  regole: RegolePeriodo,
): void {
  const ambito = ambitoDi(dati, regole)
  const perId = new Map(dati.contatori.map((c) => [c.id, c]))
  for (const c of consumi) {
    const quadro = QUADRO_DEL_CONTATORE[c.codice]
    if (quadro === undefined) continue
    const configurato = perId.get(c.contatoreId)
    aggiungiRiga(q, esclusi, quadro, {
      ambito,
      mese: c.mese,
      matricola: c.matricola,
      lettA: c.lettA,
      lettP: c.lettP,
      diffLett: c.diffLett,
      costLett: c.costLett,
      kwh: c.kwh,
      tipologia: configurato?.tipologiaConsumo,
      tipoScambio: configurato?.tipoScambio,
      idControparte: configurato?.idControparte,
      codCat: dati.codiceCatastale,
    })
  }
}

/**
 * Le righe di un venditore arrivano gia' per tipologia: quadro dalla lettera,
 * progressivo dal numero, ambito dalla provincia. `M4+M5+M6+M7` si ripartisce
 * qui, riga per riga, con le utenze della riga: righe di province diverse sono
 * clienti diversi e non condividono la soglia. Poi si sommano le righe con la
 * stessa chiave, perche' il tracciato vieta record duplicati per
 * AMBITO-MESE-PROGR-RETT.
 */
function righeDaVendite(
  q: QuadriInCostruzione,
  esclusi: ReadonlySet<QuadroModello>,
  dati: DatiPeriodo,
  regole: RegolePeriodo,
): Diagnostica[] {
  const diagnostiche: Diagnostica[] = []
  const s = regole.scaglioni
  const perChiave = new Map<string, RigaMutabile & { quadro: QuadroModello }>()
  for (const v of dati.vendite ?? []) {
    // Niente ambito predefinito: una sigla sbagliata dichiarerebbe accisa
    // nell'ambito ordinario invece che in quello speciale.
    const ambito = regole.ambiti.perProvincia[v.provincia]
    if (ambito === undefined) {
      diagnostiche.push(
        bloccante('PROVINCIA_SCONOSCIUTA', `provincia ${v.provincia} sconosciuta nella vendita del mese ${v.mese}`, {
          mese: v.mese,
        }),
      )
      continue
    }
    const base: RigaMutabile = { ambito, mese: v.mese, numeroUtenze: v.numeroUtenze, rett: 0, kwh: v.kwh, tipologia: v.tipologia }
    const righe =
      s !== undefined && s.tipologieSoggette.includes(v.tipologia)
        ? ripartisciScaglioni([base], s, s.moltiplicaPerUtenze ? v.numeroUtenze : 1)
        : [base]
    for (const r of righe) {
      const tipologia = String(r.tipologia)
      const quadro = tipologia.charAt(0) as QuadroModello
      const progr = Number(tipologia.slice(1))
      const chiave = `${quadro}|${ambito}|${v.mese}|${progr}`
      const gia = perChiave.get(chiave)
      if (gia === undefined) perChiave.set(chiave, { ...r, quadro, progr })
      else {
        gia.kwh = dec(gia.kwh ?? 0).plus(dec(r.kwh ?? 0))
        gia.numeroUtenze = Number(gia.numeroUtenze ?? 0) + Number(r.numeroUtenze ?? 0)
      }
    }
  }
  for (const { quadro, ...riga } of perChiave.values()) aggiungiRiga(q, esclusi, quadro, riga)
  return diagnostiche
}

function righeDaElenchi(q: QuadriInCostruzione, esclusi: ReadonlySet<QuadroModello>, dati: DatiPeriodo): void {
  for (const n of dati.elenchi.N ?? []) {
    aggiungiRiga(q, esclusi, 'N', { tipologiaVendita: n.tipologia, id: n.id, kwh: n.kwh })
  }
  for (const t of dati.elenchi.T ?? []) {
    aggiungiRiga(q, esclusi, 'T', { provenienza: t.provenienza, id: t.id, kwh: t.kwh })
  }
  if (dati.elenchi.perdite !== undefined) aggiungiRiga(q, esclusi, 'Perdite', { kwh: dati.elenchi.perdite })
}

export function componiQuadri(
  consumi: readonly ConsumoMensile[],
  dati: DatiPeriodo,
  regole: RegolePeriodo,
): EsitoQuadri {
  const diagnostiche: Diagnostica[] = []
  const esclusi: ReadonlySet<QuadroModello> = new Set(regole.quadriEsclusi[dati.tipologia] ?? [])
  const q: QuadriInCostruzione = {}

  righeDaContatori(q, esclusi, consumi, dati, regole)
  diagnostiche.push(...righeDaFormule(q, esclusi, consumi, dati, regole))
  diagnostiche.push(...righeDaVendite(q, esclusi, dati, regole))
  diagnostiche.push(...applicaOverride(q, dati))
  applicaScaglioni(q, dati, regole)
  diagnostiche.push(...applicaAccisa(q, regole))
  righeDaElenchi(q, esclusi, dati)

  return { modello: q, diagnostiche }
}
